import * as React from 'react'
import { connect } from 'react-redux'
import { updateSettings } from '../../store/authentication/actions'
import SettingsController, { ISettingsForm } from '../../controllers/SettingsController'
import { AnyAction, Dispatch } from 'redux';
import { AppState } from '../../store'
import TooltipError from '../../components/Tooltips/ErrorTooltip'
import Button from '../../components/Button/Button'
import Notification from '../../utilities/Notification'
import { DEFAULT_LOCALE } from '../../Constants'
import Panel from '../../components/Panel/Panel'
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount';
import ListLoader from '../../components/Loaders/ListLoader';
import { Helmet } from 'react-helmet';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import CheckboxInput from '../../components/Form/CheckboxInput';
import EditorContainer from '../../components/Editor/EditorContainer';
import Editor, { MINIMAL_EDITOR_CONFIG } from '../../components/Editor/Editor';
import LocaleHelper from '../../helpers/LocaleHelper';
import PowerSelect from '../../components/Form/PowerSelect';
import ReactSelectTheme from '../../components/Form/ReactSelectTheme';
import MobilityHelper from '../../helpers/MobilityHelper';
import { CurrentUser, DisplayableError, Locale, Settings } from '../../types';
import { showConfirmModal, showFinancialYearModal } from '../../store/modals/actions';
import Alert from '../../components/Alert/Alert';
import LedgerItemHelper from '../../helpers/LedgerItemHelper';
import Title from '../../components/Settings/Title';
import SettingHelper from '../../helpers/SettingHelper';

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  updateSettings: typeof updateSettings
  showFinancialYearModal: typeof showFinancialYearModal
  showConfirmModal: typeof showConfirmModal
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation
interface IState {
  settings: Settings
  errors: DisplayableError[]
  form: ISettingsForm | null
  isUpdating: boolean
}

class IncomingSelfBillingOrders extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    const { currentUser } = props
    const { workspace: { setting } } = currentUser

    this.state = {
      settings: setting,
      errors: [],
      form: null,
      isUpdating: false,
    }

    this.onFormSubmit = this.onFormSubmit.bind(this)
    this.onToggleEnableIncomingSelfBillingOrders = this.onToggleEnableIncomingSelfBillingOrders.bind(this)
  }

  componentDidMount(): void {
    this.fetchSettings().catch(console.error)
  }

  async fetchSettings() {
    try {
      const form = await SettingsController.getForm()
      this.setState({ form: form })
    } catch (ex) {
      console.error(ex)
    }
  }

  async onFormSubmit(e) {
    e.preventDefault()
    const { settings } = this.state
    const { t } = this.props

    this.setState({ isUpdating: true })
    try {
      const response = await SettingsController.update(settings)
      if (response.errors) {
        Notification.notifyError(t('SettingIncomingSelfBillingOrders::Oops something went wrong.'));

        this.setState({ errors: response.errors })
      } else {
        // Cast to settings
        const settings = response as Settings
        // Update local
        this.setState({ settings: settings })

        Notification.notifySuccess(t('SettingIncomingSelfBillingOrders::Settings updated successfully'));
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      this.setState({ isUpdating: false })
    }
  }

  onToggleEnableIncomingSelfBillingOrders() {
    const { settings } = this.state

    this.setState({
      settings: {
        ...settings,
        incoming_self_billing_orders_enabled: !settings.incoming_self_billing_orders_enabled
      }
    })
  }

  render() {
    const { t } = this.props
    const { settings, errors, form, isUpdating } = this.state

    if (form) {
      return (
        <>
          <Helmet>
            <title>{t('SettingIncomingSelfBillingOrders::{{__appName}} | Incoming self billing orders')}</title>
          </Helmet>
          <ScrollToTopOnMount />

          <form onSubmit={this.onFormSubmit}>
            <Title>{t('SettingIncomingSelfBillingOrders::Incoming self billing orders')}</Title>

            <Panel>
              <div className='grid'>
                <div className='grid-cell with-4col'>
                  <div className='form-item'>
                    <CheckboxInput
                      onChange={this.onToggleEnableIncomingSelfBillingOrders}
                      checked={settings.incoming_self_billing_orders_enabled}
                      label={t('SettingIncomingSelfBillingOrders::Enabled')}
                    />
                  </div>
                </div>
              </div>

              <input type='submit' style={{ display: 'none' }} />
              <div className='field-actions'>
                <input type='submit' style={{ display: 'none' }} />
                <div className='popover-wrapper'>
                  <TooltipError
                    errors={errors}
                    onDismiss={() => this.setState({ errors: [] })}
                  />
                  <Button type='success' text={t('SettingIncomingSelfBillingOrders::Save')} isLoading={isUpdating} onClick={this.onFormSubmit} />
                </div>
              </div>
            </Panel>
          </form>
        </>
      )
    }
    else {
      return (
        <Panel>
          <ListLoader />
        </Panel>
      )
    }
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    }
  } = state

  return {
    currentUser: currentUser,
  }
}


const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    updateSettings: (settings: Settings) => dispatch(updateSettings(settings)),
    showFinancialYearModal: (options) => dispatch(showFinancialYearModal(options)),
    showConfirmModal: (options) => dispatch(showConfirmModal(options))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(IncomingSelfBillingOrders))